import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Target,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from "recharts";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#0e7490', '#0f766e', '#f97316', '#8b5cf6', '#ec4899', '#10b981'];

export default function AnalyticsPage({ user, logout }) {
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [statsRes, funnelRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, { withCredentials: true }),
        axios.get(`${API}/analytics/lead-funnel`, { withCredentials: true }),
        axios.get(`${API}/analytics/campaign-performance`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setFunnel(funnelRes.data.funnel || []);
      setCampaigns(campaignsRes.data.campaigns || []);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: "Total Leads", 
      value: stats?.total_leads || 0, 
      change: `+${stats?.leads_today || 0} today`,
      positive: true,
      icon: Users,
      color: "bg-blue-500"
    },
    { 
      title: "Conversion Rate", 
      value: `${stats?.conversion_rate || 0}%`, 
      change: "vs last month",
      positive: true,
      icon: Target,
      color: "bg-green-500"
    },
    { 
      title: "Converted Leads", 
      value: stats?.converted_leads || 0, 
      change: "Total customers",
      positive: true,
      icon: TrendingUp,
      color: "bg-purple-500"
    },
    { 
      title: "Active Products", 
      value: stats?.total_products || 0, 
      change: "In catalog",
      positive: true,
      icon: BarChart3,
      color: "bg-orange-500"
    },
  ];

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Analytics</h1>
          <p className="text-slate-600 mt-1">Track your performance and growth metrics</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <Card key={index} className="stat-card">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                    <p className="stat-value mt-2">{stat.value}</p>
                    <div className="flex items-center gap-1 mt-2">
                      {stat.positive ? (
                        <ArrowUpRight className="w-4 h-4 text-green-500" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 text-red-500" />
                      )}
                      <span className="text-sm text-slate-600">{stat.change}</span>
                    </div>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Lead Funnel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-[Manrope]">Lead Funnel</CardTitle>
            </CardHeader>
            <CardContent>
              {funnel.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-slate-500">
                  No funnel data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={funnel} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" stroke="#64748b" fontSize={12} />
                    <YAxis 
                      dataKey="stage" 
                      type="category" 
                      stroke="#64748b" 
                      fontSize={12}
                      tickFormatter={(value) => value.replace('_', ' ')}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        borderRadius: '8px', 
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Bar dataKey="count" fill="#0e7490" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Lead Sources Pie */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-[Manrope]">Lead Sources Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.lead_sources?.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-slate-500">
                  No source data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={stats?.lead_sources || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="count"
                      nameKey="source"
                      label={({ source, percent }) => `${source} ${(percent * 100).toFixed(0)}%`}
                    >
                      {stats?.lead_sources?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        borderRadius: '8px', 
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Campaign Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-[Manrope]">Campaign Performance</CardTitle>
          </CardHeader>
          <CardContent>
            {campaigns.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No campaign data available</p>
                <p className="text-sm mt-1">Start tracking campaigns to see performance metrics</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Total Leads</th>
                      <th>Converted</th>
                      <th>Conversion Rate</th>
                      <th>Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((campaign, index) => (
                      <tr key={index}>
                        <td>
                          <span className="font-medium">{campaign.campaign}</span>
                        </td>
                        <td>{campaign.total_leads}</td>
                        <td>{campaign.converted}</td>
                        <td>
                          <span className={`font-medium ${
                            campaign.conversion_rate >= 20 ? 'text-green-600' : 
                            campaign.conversion_rate >= 10 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {campaign.conversion_rate}%
                          </span>
                        </td>
                        <td>
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div 
                              className="bg-cyan-700 h-2 rounded-full"
                              style={{ width: `${Math.min(campaign.conversion_rate * 5, 100)}%` }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lead Stage Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-[Manrope]">Lead Stage Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {stats?.lead_stages?.map((stage, index) => (
                <div 
                  key={index}
                  className="p-4 rounded-xl text-center"
                  style={{ backgroundColor: `${COLORS[index % COLORS.length]}15` }}
                >
                  <p className="text-3xl font-bold" style={{ color: COLORS[index % COLORS.length] }}>
                    {stage.count}
                  </p>
                  <p className="text-sm text-slate-600 capitalize mt-1">
                    {stage.stage?.replace('_', ' ')}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
